import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PersonaCustomizer } from '../components/personas/PersonaCustomizer';
import * as personaService from '../services/personaService';
import { PersonaCreate, PersonaUpdate } from '../types/persona';
import { useTheme } from '../contexts/ThemeContext';

export const PersonaForm: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { darkMode } = useTheme();
  const isEditMode = !!personaId;
  
  const { data: persona, isLoading, error } = useQuery(
    ['persona', personaId],
    () => personaId ? personaService.getPersona(personaId) : Promise.reject('No persona ID'),
    { enabled: isEditMode }
  );
  
  const createMutation = useMutation(
    (newPersona: PersonaCreate) => personaService.createPersona(newPersona),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['personas']);
        navigate(`/personas/${data.id}`);
      }
    }
  );
  
  const updateMutation = useMutation(
    (updatedPersona: PersonaUpdate) => personaService.updatePersona(updatedPersona),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['personas']);
        queryClient.invalidateQueries(['persona', data.id]);
        navigate(`/personas/${data.id}`);
      }
    }
  );
  
  const handleSave = (personaData: PersonaCreate | PersonaUpdate) => {
    if (isEditMode && 'id' in personaData) {
      updateMutation.mutate(personaData as PersonaUpdate);
    } else {
      createMutation.mutate(personaData as PersonaCreate);
    }
  };
  
  if (isEditMode && isLoading) {
    return <div className={`text-center py-10 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading persona...</div>;
  }
  
  if (isEditMode && (error || !persona)) {
    return <div className={`text-center py-10 text-red-600 ${darkMode ? 'bg-red-900 bg-opacity-20' : ''}`}>Error loading persona</div>;
  }
  
  return (
    <div className={darkMode ? 'text-gray-200' : 'text-gray-800'}>
      <h1 className="text-3xl font-bold mb-6">
        {isEditMode ? 'Edit Persona' : 'Create New Persona'}
      </h1>
      
      <PersonaCustomizer 
        initialPersona={persona} 
        onSave={handleSave}
        onCancel={() => navigate(isEditMode ? `/personas/${personaId}` : '/personas')}
        isSaving={createMutation.isLoading || updateMutation.isLoading}
        error={createMutation.error || updateMutation.error}
      />
    </div>
  );
};
